import { useCallback, useMemo, useRef } from 'react'
import {
  type AiChatFileAttachmentConfig,
  F0AiChatTextArea,
  F0Card,
  F0Heading,
  F0Text,
  type UploadedFile,
} from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import { Sparkles } from '@factorialco/f0-react/icons/app'
import { TicketingTopCategoriesForCreateDialogDocument } from 'generated/resources/graphql'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface CreateWithAiTabProps {
  active: boolean
  isOpen: boolean
  // Attachment pipeline supplied by the AI chat integration; when omitted the
  // composer simply hides the attach affordance.
  fileAttachments?: AiChatFileAttachmentConfig
  onLaunchAiTicketCreation: (
    message: string,
    options?: { source?: 'description' | 'category'; files?: UploadedFile[] }
  ) => void
}

export const CreateWithAiTab = ({
  active,
  isOpen,
  fileAttachments,
  onLaunchAiTicketCreation,
}: CreateWithAiTabProps) => {
  // F0AiChatTextArea requires a ref; we don't otherwise use it.
  // NOTE: the composer's textbox is currently unnamed for assistive tech —
  // F0AiChatTextArea exposes no labeling prop, so this is being fixed in f0.
  const composerRef = useRef<HTMLDivElement>(null)

  const { data, loading } = useQuery(TicketingTopCategoriesForCreateDialogDocument, {
    variables: { limit: 3 },
    skip: !isOpen || !active,
  })

  const topCategories = useMemo(() => data?.ticketing?.topCategoriesConnection.nodes ?? [], [data])
  const showTopCategoriesSkeleton = loading && topCategories.length === 0

  const submitPrompt = useCallback(
    (message: string, source: 'description' | 'category', files: UploadedFile[] = []) => {
      const trimmedMessage = message.trim()
      if (!trimmedMessage && files.length === 0) return

      onLaunchAiTicketCreation(trimmedMessage, { source, files })
    },
    [onLaunchAiTicketCreation]
  )

  const handlePresetClick = useCallback(
    (categoryName: string) => {
      const message = i18n.t('ticketing.inbox.create.ai_preset_prompt', { categoryName })
      submitPrompt(message, 'category')
    },
    [submitPrompt]
  )

  return (
    <Stack
      paddingX="p-8"
      paddingY="p-8"
      gap="4"
      data-testid="ticket-ai-create-form"
      style={{ maxWidth: 640, margin: '0 auto' }}
    >
      <F0Heading content={i18n.t('ticketing.inbox.create.ai_prompt_label')} />

      {/* The composer insets its own box; pull it back so it lines up flush
          with the heading and preset cards. */}
      <div style={{ marginLeft: '-16px', marginRight: '-16px' }}>
        <F0AiChatTextArea
          // Remount on each open so the composer starts empty.
          key={isOpen ? 'open' : 'closed'}
          ref={composerRef}
          onSubmit={({ text, files }) => submitPrompt(text, 'description', files)}
          inProgress={false}
          placeholders={[i18n.t('ticketing.inbox.create.ai_prompt_placeholder')]}
          fileAttachments={fileAttachments}
        />
      </div>

      {(showTopCategoriesSkeleton || topCategories.length > 0) && (
        <Stack gap="2">
          <F0Text
            variant="description"
            content={i18n.t('ticketing.inbox.create.ai_presets_label')}
          />
          <Stack gap="2">
            {showTopCategoriesSkeleton
              ? Array.from({ length: 3 }).map((_, index) => (
                  <Stack
                    key={`top-category-skeleton-${index}`}
                    aria-hidden
                    className="animate-pulse rounded-lg border border-solid border-f1-border-secondary bg-f1-background p-3"
                    gap="2"
                  >
                    <div className="h-4 w-1/3 rounded bg-f1-background-secondary" />
                  </Stack>
                ))
              : topCategories.map((category) => (
                  <F0Card
                    key={category.id}
                    avatar={{ type: 'icon', icon: Sparkles }}
                    title={category.name}
                    description={category.description || ''}
                    onClick={() => handlePresetClick(category.name)}
                    compact
                  />
                ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
